import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  IconUser,
  IconMail,
  IconCalendar,
  IconShield,
  IconEdit,
  IconDeviceFloppy,
  IconX,
  IconSettings,
  IconBrandDiscord,
  IconLink,
  IconLinkOff
} from '@tabler/icons-react';
import toast from 'react-hot-toast';
import OfficerPnlPanel from '../components/finance/OfficerPnl';

const Profile = () => {
  const { user, logout, valetProfile } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phone: '',
    address: '',
    bio: ''
  });

  // Discord connection state
  const [discordLinked, setDiscordLinked] = useState(false);
  const [discordUsername, setDiscordUsername] = useState('');
  const [discordLoading, setDiscordLoading] = useState(false);

  // Load Discord connection status from valet profile
  useEffect(() => {
    if (valetProfile) {
      setDiscordLinked(!!valetProfile.discord_user_id);
      setDiscordUsername(valetProfile.discord_username || '');
    }
  }, [valetProfile]);

  const handleLinkDiscord = async () => {
    setDiscordLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: window.location.origin + '/profile',
          scopes: 'identify email',
        },
      });
      if (error) {
        console.error('Discord link error:', error);
        toast.error('Failed to connect Discord');
      }
    } catch (err) {
      console.error('Discord link error:', err);
      toast.error('Failed to connect Discord');
    } finally {
      setDiscordLoading(false);
    }
  };

  const handleUnlinkDiscord = async () => {
    if (!valetProfile?.id) return;
    setDiscordLoading(true);
    try {
      const { error } = await supabase
        .from('valets')
        .update({ discord_user_id: null, discord_username: null })
        .eq('id', valetProfile.id);

      if (error) {
        console.error('Discord unlink error:', error);
        toast.error('Failed to disconnect Discord');
      } else {
        setDiscordLinked(false);
        setDiscordUsername('');
        toast.success('Discord disconnected');
      }
    } catch (err) {
      console.error('Discord unlink error:', err);
      toast.error('Failed to disconnect Discord');
    } finally {
      setDiscordLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      // Load profile data directly from auth user
      const authData = {
        displayName: user.displayName || user.user_metadata?.full_name || '',
        email: user.email || '',
        phone: user.user_metadata?.phone || '',
        address: user.user_metadata?.address || '',
        bio: user.user_metadata?.bio || ''
      };

      // Check for any locally saved data
      try {
        const savedData = localStorage.getItem('profile-data');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          // Merge saved data with auth data (saved data takes precedence)
          const loadedData = {
            ...authData,
            ...parsedData,
            email: authData.email // Always keep email from auth
          };
          setProfileData(loadedData);
        } else {
          setProfileData(authData);
        }
      } catch (error) {
        console.error('Error loading saved profile data:', error);
        setProfileData(authData);
      }

      setDataLoading(false);
    } else {
      setDataLoading(false);
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log('Saving profile data:', profileData);
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Save operation timed out after 10 seconds')), 10000);
      });

      // Update auth user metadata with timeout
      const savePromise = supabase.auth.updateUser({
        data: {
          full_name: profileData.displayName,
          phone: profileData.phone,
          address: profileData.address,
          bio: profileData.bio
        }
      });

      const { error: authError } = await Promise.race([savePromise, timeoutPromise]);

      if (authError) {
        console.error('Error updating auth metadata:', authError);
        throw authError;
      }

      console.log('Profile updated successfully');
      setIsEditing(false);
      toast.success('Profile updated successfully!');
      
    } catch (error) {
      console.error('Failed to update profile:', error);
      
      // Fallback: Save to localStorage
      try {
        localStorage.setItem('profile-data', JSON.stringify(profileData));
        console.log('Profile data saved to localStorage as fallback');
        toast('Profile updated locally (saved to browser storage). Some changes may not sync until connection is restored.');
        setIsEditing(false);
      } catch (localError) {
        console.error('Failed to save to localStorage:', localError);
        toast.error(`Failed to update profile: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original data
    if (user) {
      setProfileData({
        displayName: user.displayName || user.user_metadata?.full_name || '',
        email: user.email || '',
        phone: user.user_metadata?.phone || '',
        address: user.user_metadata?.address || '',
        bio: user.user_metadata?.bio || ''
      });
    }
    setIsEditing(false);
  };

  const handleLogout = () => {
    console.log('🔄 Starting logout process...');
    
    // Clear profile data
    localStorage.removeItem('profile-data');
    
    // Call logout (now synchronous)
    logout();
    
    // Navigate to landing page immediately
    navigate('/landing');
    
    console.log('✅ Successfully logged out');
  };

  if (!user) {
    return (
      <div className="h-full w-full p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Please sign in to view your profile</h1>
            <button
              onClick={() => navigate('/landing')}
              className="px-6 py-3 bg-vegas-gold text-black rounded-lg font-medium hover:bg-vegas-gold/90 transition-colors"
            >
              Go to Landing
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (dataLoading) {
    return (
      <div className="h-full w-full p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vegas-gold mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-white mb-4">Loading profile data...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-vegas-gold/20 rounded-lg">
              <IconUser className="w-8 h-8 text-vegas-gold" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Profile Information</h1>
              <p className="text-gray-400">Manage your account settings and preferences</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <IconX className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-vegas-gold text-black rounded-lg hover:bg-vegas-gold/90 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <IconDeviceFloppy className="w-4 h-4" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-vegas-gold text-black rounded-lg hover:bg-vegas-gold/90 transition-colors flex items-center gap-2"
              >
                <IconEdit className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <OfficerPnlPanel />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <IconUser className="w-5 h-5 text-vegas-gold" />
                Basic Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.displayName || ''}
                      onChange={(e) => setProfileData({...profileData, displayName: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-vegas-gold"
                      placeholder="Enter your display name"
                    />
                  ) : (
                    <p className="text-white">{profileData.displayName || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-vegas-gold"
                    />
                  ) : (
                    <p className="text-white">{profileData.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={profileData.phone || ''}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-vegas-gold"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <p className="text-white">{profileData.phone || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.address || ''}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-vegas-gold"
                      placeholder="Enter your address"
                    />
                  ) : (
                    <p className="text-white">{profileData.address || 'Not set'}</p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">Bio</label>
                {isEditing ? (
                  <textarea
                    value={profileData.bio || ''}
                    onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-vegas-gold"
                    placeholder="Tell us about yourself..."
                  />
                ) : (
                  <p className="text-white">{profileData.bio || 'No bio provided'}</p>
                )}
              </div>
            </div>

            {/* Account Information */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <IconShield className="w-5 h-5 text-vegas-gold" />
                Account Information
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <IconUser className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-white font-medium">User ID</p>
                      <p className="text-gray-400 text-sm">{user.id}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <IconCalendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-white font-medium">Member Since</p>
                      <p className="text-gray-400 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <IconMail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-white font-medium">Email Verified</p>
                      <p className="text-gray-400 text-sm">
                        {user.email_confirmed_at ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Picture */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
              <div className="w-24 h-24 bg-vegas-gold/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <IconUser className="w-12 h-12 text-vegas-gold" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-white">{profileData.displayName || 'User'}</h3>
              <p className="text-gray-400 text-sm">{profileData.email}</p>
            </div>

            {/* Connected Accounts */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <IconLink className="w-5 h-5 text-vegas-gold" />
                Connected Accounts
              </h3>

              <div className="space-y-3">
                {/* Discord */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconBrandDiscord className="w-5 h-5 text-[#5865F2]" />
                    <div>
                      <p className="text-white text-sm font-medium">Discord</p>
                      {discordLinked ? (
                        <p className="text-gray-400 text-xs">{discordUsername || 'Connected'}</p>
                      ) : (
                        <p className="text-gray-500 text-xs">Not connected</p>
                      )}
                    </div>
                  </div>
                  {discordLinked ? (
                    <button
                      onClick={handleUnlinkDiscord}
                      disabled={discordLoading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <IconLinkOff className="w-3.5 h-3.5" />
                      Unlink
                    </button>
                  ) : (
                    <button
                      onClick={handleLinkDiscord}
                      disabled={discordLoading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#5865F2] hover:bg-[#5865F2]/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <IconLink className="w-3.5 h-3.5" />
                      Link
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <IconSettings className="w-5 h-5 text-vegas-gold" />
                Actions
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={handleLogout}
                  disabled={loading}
                  className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                  ) : (
                    <IconX className="w-4 h-4" />
                  )}
                  {loading ? 'Signing Out...' : 'Sign Out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
